/**
 * THE ARTWORK BUILD NAMESPACE — one module owns it.
 *
 * ⚠️ THE TRAP THIS CLOSES. An artwork build was reachable through TWO endpoint
 * families, and both were live, and both were called from the same page:
 *
 *   CANONICAL (the Builder)   POST /api/artwork/render
 *                             GET  /api/artwork/build/:id
 *                             POST /api/artwork/build/:id/status
 *   LEGACY    (the Composer)  POST  /api/artwork/builds
 *                             POST  /api/artwork/builds/:id/renders
 *                             PATCH /api/artwork/builds/:id
 *
 * Singular versus plural, one concept, two shapes — and the split was invisible
 * because each surface only ever touched its own half. Two operators building
 * the same ring on two tabs of the same page produced records in two different
 * namespaces, which is the version of this bug that costs money: nothing
 * reconciles, and the second record looks like a missing one.
 *
 * WHAT IS ACTUALLY SHARED, and it is the reason this is a module rather than a
 * find-and-replace: `GET /api/artwork/builds` — the LIST — is used by BOTH.
 * `StudioBuilds.jsx` (the Builder's own history tab) lists from the plural
 * route and then reads each record from the singular one. So the plural family
 * was never "the Composer's"; the client had simply grown into both and nobody
 * owned the boundary.
 *
 * SO: THE CLIENT CONVERGES HERE, NOT ON THE BACKEND. `sr-portal-backend` is a
 * separate repo and is not modified from this one (hard rule #4). Every
 * artwork-build call in the app now goes through this file, the canonical set
 * is what new code uses, and the legacy write set is exported under names that
 * say what they are. Retiring the legacy routes server-side is specced in
 * `docs/BACKEND-HANDOVER.md` §1 — it is a backend decision, and until it is
 * made, silently repointing the Composer's writes at routes that may not accept
 * its payload would break the one surface that renders today.
 */

import { api } from '../../lib/api.js'

/* ------------------------------------------------------------------ *
 * CANONICAL — the Builder's namespace. New code uses these.
 * ------------------------------------------------------------------ */

/** Assemble + render a build. The write; audited via `request()`. */
export const renderBuild = (payload) => api.artworkRender(payload)

/** Read one build record (poll target while `renderPending`). */
export const getBuild = (buildId, opts) => api.artworkBuildGet(buildId, opts)

/** Advance draft → proof → submitted → production. Human-gated, one step. */
export const advanceBuildStatus = (buildId, body, opts) =>
  api.artworkBuildStatus(buildId, body, opts)

/**
 * List build records. SHARED by both surfaces — this is the one route that was
 * genuinely common, and it stays on the plural path because that is where the
 * records are.
 */
export const listBuilds = (query, opts) => api.artworkBuilds(query, opts)

/* ------------------------------------------------------------------ *
 * LEGACY — the Composer's write set. Named so it cannot be picked up by
 * accident, and referenced from exactly one file (`engine/buildRecord.js`).
 * ------------------------------------------------------------------ */

/** @deprecated Legacy plural namespace — see docs/BACKEND-HANDOVER.md §1. */
export const LEGACY_createBuildRecord = (snapshot) => api.artworkBuildCreate(snapshot)

/** @deprecated Legacy plural namespace — see docs/BACKEND-HANDOVER.md §1. */
export const LEGACY_attachRender = (buildId, body) => api.artworkBuildAttach(buildId, body)

/** @deprecated Legacy plural namespace — see docs/BACKEND-HANDOVER.md §1. */
export const LEGACY_updateBuild = (buildId, body) => api.artworkBuildUpdate(buildId, body)

/** The two families, for anything that needs to explain the split on screen. */
export const NAMESPACES = {
  canonical: ['POST /api/artwork/render', 'GET /api/artwork/build/:id', 'POST /api/artwork/build/:id/status'],
  shared: ['GET /api/artwork/builds'],
  legacy: ['POST /api/artwork/builds', 'POST /api/artwork/builds/:id/renders', 'PATCH /api/artwork/builds/:id'],
}
